import { useTranslation } from 'react-i18next';
import { STEP_ACTION_ICONS } from './step-action-icons';
import type {
  AssigneeModel,
  PopulatedExternalContact,
  PopulatedOrgFunction,
  PopulatedSystem,
  PopulatedUser,
  StepActionType
} from '@bliks-io/contracts';
import { cn } from '@bliks-io/ui';
import { EntityHeader } from './EntityHeader';

interface StepCardProps {
  step: {
    actionType: StepActionType;
    generatedName?: string | null;
    generatedDescription?: string | null;
    task?: {
      taskDescription?: string;
    };
    assignedTo?: PopulatedUser | PopulatedSystem | PopulatedOrgFunction | PopulatedExternalContact;
    assignedToModel?: AssigneeModel;
  };
  /** The step's outcome. For a decision this is the matched rule's output,
   *  resolved by the caller from the routing rule ids (see `resolveStepOutput`). */
  output?: string | null;
  className?: string;
}

export function StepCard({ step, output, className }: StepCardProps) {
  const { t } = useTranslation();

  const { actionType, generatedName, generatedDescription, task, assignedTo, assignedToModel } =
    step;

  const description =
    generatedDescription || (actionType === 'task' ? task?.taskDescription : null);

  const StepIcon = STEP_ACTION_ICONS[actionType];

  return (
    <div className={cn('bg-white border border-border rounded-lg p-3 w-[340px]', className)}>
      {assignedTo && (
        <>
          <EntityHeader entity={assignedTo} entityType={assignedToModel} />
          <div className="h-px w-full bg-border my-2" />
        </>
      )}

      <div className="flex items-center gap-2 mb-2">
        <StepIcon
          className={cn(
            'h-4 w-4 shrink-0',
            actionType === 'decision' ? 'text-highlight' : 'text-info'
          )}
        />
        {generatedName && <span className="text-xs font-medium">{generatedName}</span>}
      </div>

      {description && <div className="text-xs text-muted-foreground px-2">{description}</div>}

      {output && (
        <div className="text-xs mt-2 p-2 rounded-md bg-muted/50">
          <span className="font-medium">{t('assignmentReview.outputLabel')} </span>
          <span className="text-muted-foreground">{output}</span>
        </div>
      )}
    </div>
  );
}
